import React, {useEffect} from 'react';
import {OfficeBuildingIcon} from '@heroicons/react/solid';

import AddInterviewCard from './AddInterviewCard';
import BlogPostCard from '../../components/BlogPostCard';
import CoachCard from './CoachCard';
import CourseCard from '../../components/CourseCard';
import GuideCard from './GuideCard';
import QuestionCard from './QuestionCard';
import GetReferredCard from './GetReferredCard';
import Button from '../../components/Button';
import Link from '../../components/Link';
import DocumentHeader from '../../components/DocumentHeader';
import {Breadcrumb} from '../../components/LinkedData';
import {CaretIcon} from '../../components/svg';
import FollowButton, {ButtonSizes} from '../../components/FollowButton';
import {useAppContext, useAnalytics} from '../../hooks';
import {shouldHideCoaching} from '../../util';

const MAX_GUIDES = 4;
const MAX_COURSES = 2;
const MAX_QUESTIONS = 3;
const MAX_COACHES = 2;
const MAX_BLOG_POSTS = 2;

// eslint-disable-next-line jsx-a11y/heading-has-content
const H2 = (props) => <h2 className="text-xl font-bold text-gray-900 mb-4" {...props} />;

// eslint-disable-next-line jsx-a11y/heading-has-content
const SidebarH2 = (props) => <h2 className="text-lg font-bold text-gray-900 mb-3" {...props} />;

const Grid = (props) => <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-4" {...props} />;

const SidebarGrid = (props) => <div className="grid grid-cols-1 gap-2 md:gap-4" {...props} />;

const SeeMore = (props) => <Link className="inline-block font-semibold text-indigo mt-4" {...props} />;


const CompanyPage = ({
  company, about, blogPosts, coaches, courses, guides, questions, numReferrers,
}) => {
  const {currentUser} = useAppContext();
  const {tracker} = useAnalytics();

  useEffect(() => {
    tracker.hubView({tag: company.id});
  }, []);

  const emptyMainSection = !(guides?.length || courses?.length || questions?.length);
  const showSidebar = Boolean(about || coaches?.length || blogPosts?.length);
  const description = `
    Prepare for the ${company.name} interview with Exponent's expertise
    on their interview process, sample questions with our expert answers,
    and essential tips from previous interviewers.
  `;

  const onClickFollowButton = (following) => (following
    ? tracker.hubFollowTag({follower: currentUser.id, tag: company.id})
    : tracker.hubUnfollowTag({follower: currentUser.id, tag: company.id})
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Metadata */}
      <DocumentHeader
        title={`Interviewing at ${company.name} (${new Date().getFullYear()})`}
        metaDescription={description}
        socialDescription={description}
      />
      <Breadcrumb items={[
        {name: 'Companies', url: '/companies'},
        {name: company.name, url: `/companies/${company.id}`},
      ]}
      />

      <div className="mx-auto max-w-6xl px-4 py-12 flex justify-center">
        <div className="md:w-2/3">

          {/* Header */}
          <div className="flex justify-between items-center mb-10">
            <div className="flex items-center">
              {company.image_url ? (
                <img src={company.image_url} alt={`${company.name} logo`} className="h-8 w-8 md:h-16 md:w-16 mr-4 object-contain" />
              ) : (
                <OfficeBuildingIcon className="h-8 w-8 md:h-16 md:w-16 mr-4 text-gray-300" />
              )}
              <div>
                <div className="flex items-center space-x-2">
                  <CaretIcon height={9} width={6} fill="#999" />
                  <Link href="/companies" className="font-medium text-gray-400">Companies</Link>
                </div>
                <h1 className="text-2xl md:text-4xl max-w-xl font-bold text-gray-900">{company.name}</h1>
              </div>
            </div>
            <div>
              <FollowButton
                tag={company}
                size={ButtonSizes.LARGE}
                onClick={onClickFollowButton}
              />
            </div>
          </div>

          {/* Mobile-only about */}
          {about && (
            <div className="block lg:hidden mb-10">
              <H2>{`About ${company.name}`}</H2>
              <span className="block text-lg text-gray-500 font-medium mb-2 max-w-xl">{about}</span>
            </div>
          )}

          {/* Empty main section placeholder */}
          {emptyMainSection && (
            <div className="bg-gray-100 rounded-lg p-5 flex justify-between items-center flex-col mb-10">
              <div className="text-lg font-medium text-gray-900 mb-3 max-w-xl sm:text-center">
                {`We don't know that much about ${company.name} yet. Share your interview experience and help get the conversation started!`}
              </div>
              <Button
                href="/questions/contribute"
                onClick={() => tracker.hubClickContribute()}
                className="flex-shrink-0 w-full sm:w-auto"
              >
                Add interview
              </Button>
            </div>
          )}

          {/* Guides */}
          {guides?.length > 0 && (
            <div className="mb-10">
              <H2>Interview Guides</H2>
              <Grid>
                {guides.map((guide, i) => (i < MAX_GUIDES && (
                  <GuideCard guide={guide} tag={company} />)))}
              </Grid>
              {guides.length > MAX_GUIDES && (
                <SeeMore
                  href={`/guides?query=${company.id}`}
                  onClick={() => tracker.hubSeeMore({type: 'guides', tag: company.id})}
                >
                  See all guides
                </SeeMore>
              )}
            </div>
          )}

          {/* Job Referrals */}
          {numReferrers > 0 && (
            <GetReferredCard
              tag={company}
              numReferrers={numReferrers}
              className="mb-10"
            />
          )}


          {/* Courses */}
          {courses?.length > 0 && (
            <div className="mb-10">
              <H2>Courses</H2>
              <Grid>
                {courses.map((course, i) => (i < MAX_COURSES && (
                  <CourseCard
                    course={course}
                    trackerEvent={() => tracker.hubClickCard({type: 'courses', tag: company.id, slug: course.slug})}
                    urlParams={{src: 'blog'}}
                  />
                )))}
              </Grid>
              {courses.length > MAX_COURSES && (
                <SeeMore
                  href={`/courses?tag=${company.name}`}
                  onClick={() => tracker.hubSeeMore({type: 'courses', tag: company.id})}
                >
                  See all courses
                </SeeMore>
              )}
            </div>
          )}

          {/* Questions */}
          {questions?.length > 0 && (
            <div className="mb-10">
              <H2>Interview Questions</H2>
              {questions.map((question, i) => (i < MAX_QUESTIONS && (
                <QuestionCard question={question} tag={company} />)))}
              <AddInterviewCard tag={company} />
              {questions.length > MAX_QUESTIONS && (
                <SeeMore
                  href={`/questions?company=${company.id}`}
                  onClick={() => tracker.hubSeeMore({type: 'questions', tag: company.id})}
                >
                  See all questions
                </SeeMore>
              )}
            </div>
          )}

          {/* Mobile-only coaches */}
          {coaches?.length > 0 && !shouldHideCoaching(currentUser) && (
            <div className="block lg:hidden mb-10">
              <H2>{`Top ${company.name} Coaches`}</H2>
              <Grid>
                {coaches.map((coach, i) => (i < MAX_COACHES && (
                  <CoachCard coach={coach} tag={company} />)))}
              </Grid>
              {coaches.length > MAX_COACHES && (
                <SeeMore
                  href={`/coaching?company=${company.id}`}
                  onClick={() => tracker.hubSeeMore({type: 'coaching', tag: company.id})}
                >
                  See all coaches
                </SeeMore>
              )}
            </div>
          )}

          {/* Mobile-only blog posts */}
          {blogPosts?.length > 0 && (
            <div className="block lg:hidden mb-10">
              <H2>Related Blog Posts</H2>
              <Grid>
                {blogPosts.map((post, i) => (i < MAX_BLOG_POSTS && (
                  <BlogPostCard
                    post={post}
                    trackerEvent={() => tracker.hubClickCard({type: 'blog', tag: company.id, slug: post.slug})}
                    urlParams={{src: 'hub'}}
                  />
                )))}
              </Grid>
              {blogPosts.length > MAX_BLOG_POSTS && (
                <SeeMore
                  href={`/blog/tag/${company.id}`}
                  onClick={() => tracker.hubSeeMore({type: 'blog', tag: company.id})}
                >
                  See all blog posts
                </SeeMore>
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        {showSidebar && (
          <div className="border-l ml-10 pl-6 w-1/3 hidden lg:block">

            {/* About */}
            {about && (
              <div className="mb-10">
                <SidebarH2>{`About ${company.name}`}</SidebarH2>
                <span className="block text-lg text-gray-500 font-medium mb-2 max-w-xl">{about}</span>
              </div>
            )}

            {/* Coaches */}
            {coaches?.length > 0 && !shouldHideCoaching(currentUser) && (
              <div className="mb-10">
                <SidebarH2>{`Top ${company.name} Coaches`}</SidebarH2>
                <SidebarGrid>
                  {coaches.map((coach, i) => (i < MAX_COACHES && (
                    <CoachCard coach={coach} tag={company} />)))}
                </SidebarGrid>
                {coaches.length > MAX_COACHES && (
                  <SeeMore
                    href={`/coaching?company=${company.id}`}
                    onClick={() => tracker.hubSeeMore({type: 'coaching', tag: company.id})}
                  >
                    See all coaches
                  </SeeMore>
                )}
              </div>
            )}

            {/* Blog posts */}
            {blogPosts?.length > 0 && (
              <div className="mb-10">
                <SidebarH2>Related Blog Posts</SidebarH2>
                <SidebarGrid>
                  {blogPosts.map((post, i) => (i < MAX_BLOG_POSTS && (
                    <BlogPostCard
                      post={post}
                      trackerEvent={() => tracker.hubClickCard({type: 'blog', tag: company.id, slug: post.slug})}
                      urlParams={{src: 'hub'}}
                    />
                  )))}
                </SidebarGrid>
                {blogPosts.length > MAX_BLOG_POSTS && (
                  <SeeMore
                    href={`/blog/tag/${company.id}`}
                    onClick={() => tracker.hubSeeMore({type: 'blog', tag: company.id})}
                  >
                    See all blog posts
                  </SeeMore>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyPage;
